import { useState, useEffect } from "react";
import { activityStore } from "../utils/dataStore";
import type { StoredNotification, NotifType, NotifStatus, TargetGroup } from "../utils/notificationStore";
import { useAdminData } from "../AdminDataContext";
import { useMemberAudienceUi } from "../hooks/useMemberAudienceUi";
import { countForTargetGroup } from "../utils/memberAudienceStatsUi";
import { reportError } from "../../../api/errors";
import {
  createTenantBroadcastNotification,
  deleteTenantBroadcastNotification,
  fetchTenantBroadcastNotifications,
} from "../../../api/broadcastNotifications";

const typeConfig: Record<NotifType, { label: string; icon: string; color: string }> = {
  announcement: { label: "公告", icon: "ri-megaphone-line", color: "bg-red-50 text-red-600" },
  event: { label: "活动", icon: "ri-calendar-event-line", color: "bg-green-50 text-green-600" },
  system: { label: "系统", icon: "ri-settings-3-line", color: "bg-gray-100 text-gray-600" },
  personal: { label: "个人", icon: "ri-user-line", color: "bg-amber-50 text-amber-600" },
  signature_remind: { label: "签署提醒", icon: "ri-pen-nib-line", color: "bg-red-50 text-red-600" },
};

const statusConfig: Record<NotifStatus, { label: string; color: string }> = {
  sent: { label: "已发送", color: "bg-green-100 text-green-700" },
  draft: { label: "草稿", color: "bg-gray-100 text-gray-500" },
  scheduled: { label: "定时发送", color: "bg-amber-100 text-amber-700" },
};

const targetGroupLabels: Record<TargetGroup, string> = {
  all: "全部会员",
  active: "活跃会员",
  new: "新会员（30天内）",
  vip: "荣誉/高级会员",
  unsigned: "未签署会员",
  level_normal: "普通会员",
  level_premium: "高级会员",
  level_director: "理事会员",
  activity_enrolled: "已报名指定活动",
  activity_not_enrolled: "未报名指定活动",
};

const directedLevelMeta = [
  { key: "level_normal" as TargetGroup, label: "普通会员", icon: "ri-shield-line", color: "bg-gray-100 text-gray-600" },
  { key: "level_premium" as TargetGroup, label: "高级会员", icon: "ri-medal-line", color: "bg-amber-50 text-amber-600" },
  { key: "level_director" as TargetGroup, label: "理事会员", icon: "ri-vip-crown-line", color: "bg-teal-50 text-teal-600" },
];

function formatAudienceCount(n: number | null) {
  if (n === null) return "…";
  return String(n);
}

type TargetMode = "group" | "directed";
type DirectedSubMode = "level" | "activity";

interface CreateForm {
  title: string;
  content: string;
  type: NotifType;
  targetMode: TargetMode;
  targetGroup: TargetGroup;
  directedSubMode: DirectedSubMode;
  directedLevel: TargetGroup;
  directedActivityId: string;
  directedActivityRelation: "enrolled" | "not_enrolled";
  scheduleType: "now" | "later";
  scheduledAt: string;
}

const defaultForm: CreateForm = {
  title: "",
  content: "",
  type: "announcement",
  targetMode: "group",
  targetGroup: "all",
  directedSubMode: "level",
  directedLevel: "level_normal",
  directedActivityId: "",
  directedActivityRelation: "enrolled",
  scheduleType: "now",
  scheduledAt: "",
};

export default function NotificationManager() {
  const { tenantLegacyId } = useAdminData();
  const { audience, loading: audienceLoading, error: audienceError } = useMemberAudienceUi();
  const [notifications, setNotifications] = useState<StoredNotification[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedNotif, setSelectedNotif] = useState<StoredNotification | null>(null);
  const [filterStatus, setFilterStatus] = useState<"all" | NotifStatus>("all");
  const [form, setForm] = useState<CreateForm>(defaultForm);
  const [sending, setSending] = useState(false);
  const [sentSuccess, setSentSuccess] = useState(false);

  const activities = activityStore.getAll().filter((a) => a.status === "ongoing" || a.status === "full");

  useEffect(() => {
    if (!tenantLegacyId) return;
    let cancelled = false;
    void fetchTenantBroadcastNotifications(tenantLegacyId)
      .then((list) => {
        if (!cancelled) setNotifications(list);
      })
      .catch((e) => {
        if (!cancelled) reportError(e, "加载通知失败");
      });
    return () => {
      cancelled = true;
    };
  }, [tenantLegacyId]);

  const filtered = filterStatus === "all" ? notifications : notifications.filter((n) => n.status === filterStatus);

  const getTargetCountForForm = (): number => {
    if (form.targetMode === "group") return countForTargetGroup(form.targetGroup, audience);
    if (form.directedSubMode === "level") return countForTargetGroup(form.directedLevel, audience);
    if (form.directedSubMode === "activity" && form.directedActivityId) {
      const act = activities.find((a) => a.id === form.directedActivityId);
      if (!act) return 0;
      return form.directedActivityRelation === "enrolled" ? act.enrolled : act.capacity - act.enrolled;
    }
    return 0;
  };

  const getTargetGroupForForm = (): TargetGroup => {
    if (form.targetMode === "group") return form.targetGroup;
    if (form.directedSubMode === "level") return form.directedLevel;
    return form.directedActivityRelation === "enrolled" ? "activity_enrolled" : "activity_not_enrolled";
  };

  const getTargetLabelForList = (notif: StoredNotification): string => {
    if (notif.targetGroup === "activity_enrolled" || notif.targetGroup === "activity_not_enrolled") {
      const rel = notif.targetGroup === "activity_enrolled" ? "已报名" : "未报名";
      return `${rel}「${notif.relatedActivityTitle ?? "指定活动"}」`;
    }
    return targetGroupLabels[notif.targetGroup] ?? notif.targetGroup;
  };

  const handleSend = async () => {
    if (!form.title || !form.content || !tenantLegacyId) return;
    const targetCount = getTargetCountForForm();
    const targetGroup = getTargetGroupForForm();
    const act = form.directedSubMode === "activity" ? activities.find((a) => a.id === form.directedActivityId) : undefined;
    setSending(true);
    try {
      await createTenantBroadcastNotification(tenantLegacyId, {
        title: form.title,
        content: form.content,
        type: form.type,
        status: form.scheduleType === "later" ? "scheduled" : "sent",
        targetGroup,
        targetCount,
        readCount: 0,
        sentAt: form.scheduleType === "now" ? new Date().toLocaleString("zh-CN") : undefined,
        scheduledAt: form.scheduleType === "later" ? form.scheduledAt || undefined : undefined,
        relatedActivityId: act?.id,
        relatedActivityTitle: act?.title,
      });
      const list = await fetchTenantBroadcastNotifications(tenantLegacyId);
      setNotifications(list);
      setSentSuccess(true);
      setTimeout(() => {
        setSentSuccess(false);
        setShowCreate(false);
        setForm(defaultForm);
      }, 1200);
    } catch (e) {
      reportError(e, "发送失败");
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!tenantLegacyId) return;
    try {
      await deleteTenantBroadcastNotification(tenantLegacyId, id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      if (selectedNotif?.id === id) setSelectedNotif(null);
    } catch (e) {
      reportError(e, "删除失败");
    }
  };

  const totalSent = notifications.filter((n) => n.status === "sent").length;
  const totalReach = notifications.filter((n) => n.status === "sent").reduce((s, n) => s + n.targetCount, 0);
  const totalRead = notifications.filter((n) => n.status === "sent").reduce((s, n) => s + n.readCount, 0);
  const avgReadRate = totalReach > 0 ? Math.round((totalRead / totalReach) * 100) : 0;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-gray-800">消息通知</h2>
          <p className="text-xs text-gray-400 mt-0.5">向会员推送公告、活动提醒等消息</p>
          {audienceError && <p className="text-xs text-red-500 mt-1">{audienceError}</p>}
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm rounded-xl hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-send-plane-line"></i> 发送通知
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3 mb-5">
        {[
          { label: "累计发送", value: totalSent, icon: "ri-send-plane-line", color: "text-red-600 bg-red-50" },
          { label: "触达会员", value: totalReach.toLocaleString(), icon: "ri-group-line", color: "text-green-600 bg-green-50" },
          { label: "已读人次", value: totalRead.toLocaleString(), icon: "ri-eye-line", color: "text-amber-600 bg-amber-50" },
          { label: "平均阅读率", value: `${avgReadRate}%`, icon: "ri-bar-chart-line", color: "text-gray-600 bg-gray-100" },
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-2xl p-4 flex items-center gap-3">
            <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${stat.color}`}>
              <i className={`${stat.icon} text-sm`}></i>
            </div>
            <div>
              <p className="text-lg font-bold text-gray-800">{stat.value}</p>
              <p className="text-xs text-gray-400">{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-5">
        {/* List */}
        <div className="flex-1 min-w-0">
          <div className="flex gap-1 mb-3 bg-gray-100 p-1 rounded-xl w-fit">
            {(["all", "sent", "scheduled", "draft"] as const).map((s) => (
              <button
                key={s}
                onClick={() => setFilterStatus(s)}
                className={`px-3 py-1.5 rounded-lg text-xs transition-all cursor-pointer whitespace-nowrap ${filterStatus === s ? "bg-white text-gray-800 font-medium shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
              >
                {s === "all" ? "全部" : statusConfig[s as NotifStatus].label}
              </button>
            ))}
          </div>

          <div className="space-y-2">
            {filtered.map((notif) => {
              const typeCfg = typeConfig[notif.type];
              const statusCfg = statusConfig[notif.status];
              const readRate = notif.targetCount > 0 ? Math.round((notif.readCount / notif.targetCount) * 100) : 0;
              const isDirected = notif.targetGroup.startsWith("level_") || notif.targetGroup.startsWith("activity_");
              return (
                <div
                  key={notif.id}
                  onClick={() => setSelectedNotif(notif)}
                  className={`bg-white rounded-2xl p-4 cursor-pointer transition-all border-2 ${selectedNotif?.id === notif.id ? "border-red-400" : "border-transparent hover:border-gray-200"}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${typeCfg.color}`}>
                      <i className={`${typeCfg.icon} text-sm`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-sm font-semibold text-gray-800 truncate">{notif.title}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${statusCfg.color}`}>{statusCfg.label}</span>
                        {isDirected && (
                          <span className="text-xs px-1.5 py-0.5 rounded-full bg-teal-50 text-teal-600 flex-shrink-0 flex items-center gap-1">
                            <i className="ri-focus-3-line"></i> 定向发送
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 line-clamp-1 mb-2">{notif.content}</p>
                      <div className="flex items-center gap-3 text-xs text-gray-400 flex-wrap">
                        <span className="flex items-center gap-1">
                          <i className="ri-group-line"></i>
                          {getTargetLabelForList(notif)}
                          <span className="text-gray-500 font-medium">（{notif.targetCount}人）</span>
                        </span>
                        {notif.status === "sent" && (
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line"></i>
                            阅读率 <span className={`font-medium ${readRate >= 60 ? "text-green-600" : "text-amber-600"}`}>{readRate}%</span>
                          </span>
                        )}
                        {notif.sentAt && <span className="flex items-center gap-1"><i className="ri-time-line"></i>{notif.sentAt}</span>}
                      </div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); void handleDelete(notif.id); }}
                      className="w-7 h-7 flex items-center justify-center text-gray-300 hover:text-red-500 cursor-pointer rounded-lg hover:bg-red-50 transition-colors flex-shrink-0"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2"><i className="ri-notification-off-line text-4xl text-gray-200"></i></div>
                <p className="text-sm">暂无通知记录</p>
              </div>
            )}
          </div>
        </div>

        {/* Detail */}
        {selectedNotif && (
          <div className="w-72 flex-shrink-0">
            <div className="bg-white rounded-2xl p-5 sticky top-0">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-800 text-sm">通知详情</h3>
                <button onClick={() => setSelectedNotif(null)} className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100"><i className="ri-close-line"></i></button>
              </div>
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className={`text-xs px-2 py-0.5 rounded-full ${typeConfig[selectedNotif.type].color}`}>{typeConfig[selectedNotif.type].label}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${statusConfig[selectedNotif.status].color}`}>{statusConfig[selectedNotif.status].label}</span>
              </div>
              <h4 className="font-semibold text-gray-800 mb-2 text-sm leading-snug">{selectedNotif.title}</h4>
              <p className="text-xs text-gray-500 leading-relaxed mb-4 bg-gray-50 rounded-xl p-3">{selectedNotif.content}</p>
              <div className="space-y-2.5 text-xs">
                <div className="flex justify-between"><span className="text-gray-400">目标群体</span><span className="text-gray-700 font-medium">{getTargetLabelForList(selectedNotif)}</span></div>
                <div className="flex justify-between"><span className="text-gray-400">发送人数</span><span className="text-gray-700 font-medium">{selectedNotif.targetCount} 人</span></div>
                {selectedNotif.status === "sent" && (
                  <>
                    <div className="flex justify-between"><span className="text-gray-400">已读人数</span><span className="text-green-600 font-medium">{selectedNotif.readCount} 人</span></div>
                    <div className="flex justify-between"><span className="text-gray-400">阅读率</span><span className="text-gray-700 font-medium">{selectedNotif.targetCount > 0 ? Math.round((selectedNotif.readCount / selectedNotif.targetCount) * 100) : 0}%</span></div>
                  </>
                )}
                {selectedNotif.sentAt && <div className="flex justify-between"><span className="text-gray-400">发送时间</span><span className="text-gray-700">{selectedNotif.sentAt}</span></div>}
                {selectedNotif.scheduledAt && <div className="flex justify-between"><span className="text-gray-400">计划发送</span><span className="text-amber-600">{selectedNotif.scheduledAt}</span></div>}
                {selectedNotif.relatedActivityTitle && (
                  <div className="flex justify-between"><span className="text-gray-400">关联活动</span><span className="text-teal-600 font-medium truncate ml-2 text-right">{selectedNotif.relatedActivityTitle}</span></div>
                )}
              </div>
              {selectedNotif.status === "sent" && (
                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-gray-400">阅读进度</span>
                    <span className="text-gray-600 font-medium">{selectedNotif.readCount}/{selectedNotif.targetCount}</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full transition-all" style={{ width: `${selectedNotif.targetCount > 0 ? (selectedNotif.readCount / selectedNotif.targetCount) * 100 : 0}%` }}></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-[600px] max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100">
              <h3 className="font-bold text-gray-800">发送通知</h3>
              <button onClick={() => setShowCreate(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100"><i className="ri-close-line text-lg"></i></button>
            </div>
            <div className="px-6 py-5 space-y-5">
              {/* 通知类型 */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">通知类型</label>
                <div className="flex gap-2 flex-wrap">
                  {(["announcement", "event", "system", "personal"] as const).map((key) => {
                    const cfg = typeConfig[key];
                    return (
                      <button key={key} onClick={() => setForm((p) => ({ ...p, type: key }))} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs border-2 cursor-pointer transition-all whitespace-nowrap ${form.type === key ? `border-red-400 ${cfg.color}` : "border-transparent bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                        <i className={cfg.icon}></i> {cfg.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 标题 */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">通知标题 *</label>
                <input type="text" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} placeholder="请输入通知标题" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400" />
              </div>

              {/* 内容 */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">通知内容 *</label>
                <textarea value={form.content} onChange={(e) => setForm((p) => ({ ...p, content: e.target.value }))} placeholder="请输入通知正文内容..." rows={3} maxLength={500} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 resize-none"></textarea>
                <p className="text-xs text-gray-400 text-right mt-1">{form.content.length}/500</p>
              </div>

              {/* 目标群体 */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">发送对象</label>
                {/* Mode Toggle */}
                <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-3 w-fit">
                  {(["group", "directed"] as const).map((mode) => (
                    <button
                      key={mode}
                      onClick={() => setForm((p) => ({ ...p, targetMode: mode }))}
                      className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs cursor-pointer transition-all whitespace-nowrap ${form.targetMode === mode ? "bg-white text-gray-800 font-medium shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                    >
                      <i className={mode === "group" ? "ri-group-line" : "ri-focus-3-line"}></i>
                      {mode === "group" ? "通用群组" : "精准定向"}
                    </button>
                  ))}
                </div>

                {form.targetMode === "group" ? (
                  <div className="space-y-1.5">
                    {(["all", "active", "new", "vip"] as const).map((key) => {
                      const label = targetGroupLabels[key];
                      const cnt = audience === null || audienceLoading ? null : countForTargetGroup(key, audience);
                      return (
                        <label key={key} className={`flex items-center justify-between p-3 rounded-xl border-2 cursor-pointer transition-all ${form.targetGroup === key ? "border-red-400 bg-red-50" : "border-gray-100 bg-gray-50 hover:border-gray-200"}`}>
                          <div className="flex items-center gap-2">
                            <input type="radio" name="targetGroup" checked={form.targetGroup === key} onChange={() => setForm((p) => ({ ...p, targetGroup: key }))} className="accent-red-600" />
                            <span className="text-sm text-gray-700">{label}</span>
                          </div>
                          <span className="text-xs text-gray-400">{formatAudienceCount(cnt)} 人</span>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-2xl p-4 space-y-4">
                    {/* Sub-mode tabs */}
                    <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
                      {(["level", "activity"] as const).map((sub) => (
                        <button
                          key={sub}
                          onClick={() => setForm((p) => ({ ...p, directedSubMode: sub }))}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs cursor-pointer transition-all whitespace-nowrap ${form.directedSubMode === sub ? "bg-white text-gray-800 font-medium shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                        >
                          <i className={sub === "level" ? "ri-medal-line" : "ri-calendar-event-line"}></i>
                          {sub === "level" ? "按会员等级" : "按活动报名"}
                        </button>
                      ))}
                    </div>

                    {form.directedSubMode === "level" ? (
                      <div className="space-y-2">
                        <p className="text-xs text-gray-400">选择要推送的会员等级：</p>
                        {directedLevelMeta.map((lv) => {
                          const cnt = audience === null || audienceLoading ? null : countForTargetGroup(lv.key, audience);
                          return (
                          <label key={lv.key} className={`flex items-center justify-between p-3 rounded-xl border-2 cursor-pointer transition-all ${form.directedLevel === lv.key ? "border-teal-400 bg-teal-50" : "border-gray-100 bg-gray-50 hover:border-gray-200"}`}>
                            <div className="flex items-center gap-2.5">
                              <input type="radio" checked={form.directedLevel === lv.key} onChange={() => setForm((p) => ({ ...p, directedLevel: lv.key }))} className="accent-teal-600" />
                              <div className={`w-7 h-7 flex items-center justify-center rounded-lg ${lv.color}`}>
                                <i className={`${lv.icon} text-sm`}></i>
                              </div>
                              <span className="text-sm text-gray-700">{lv.label}</span>
                            </div>
                            <span className="text-xs text-gray-400">{formatAudienceCount(cnt)} 人</span>
                          </label>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div>
                          <p className="text-xs text-gray-400 mb-2">选择关联活动（仅进行中）：</p>
                          {activities.length === 0 ? (
                            <div className="text-center py-4 bg-gray-50 rounded-xl">
                              <p className="text-xs text-gray-400">暂无进行中的活动</p>
                            </div>
                          ) : (
                            <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
                              {activities.map((act) => (
                                <label key={act.id} className={`flex items-center justify-between p-3 rounded-xl border-2 cursor-pointer transition-all ${form.directedActivityId === act.id ? "border-teal-400 bg-teal-50" : "border-gray-100 bg-gray-50 hover:border-gray-200"}`}>
                                  <div className="flex items-center gap-2">
                                    <input type="radio" checked={form.directedActivityId === act.id} onChange={() => setForm((p) => ({ ...p, directedActivityId: act.id }))} className="accent-teal-600" />
                                    <span className="text-sm text-gray-700 line-clamp-1">{act.title}</span>
                                  </div>
                                  <span className="text-xs text-gray-400 flex-shrink-0 ml-2">{act.enrolled} 人</span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 mb-2">报名状态筛选：</p>
                          <div className="flex gap-2">
                            {(["enrolled", "not_enrolled"] as const).map((rel) => {
                              const act = activities.find((a) => a.id === form.directedActivityId);
                              const count = act ? (rel === "enrolled" ? act.enrolled : act.capacity - act.enrolled) : 0;
                              return (
                                <button
                                  key={rel}
                                  onClick={() => setForm((p) => ({ ...p, directedActivityRelation: rel }))}
                                  className={`flex-1 py-2.5 rounded-xl text-sm border-2 cursor-pointer transition-all whitespace-nowrap ${form.directedActivityRelation === rel ? "border-teal-400 bg-teal-50 text-teal-700 font-medium" : "border-gray-100 text-gray-600 hover:border-gray-200"}`}
                                >
                                  <i className={`${rel === "enrolled" ? "ri-checkbox-circle-line" : "ri-close-circle-line"} mr-1`}></i>
                                  {rel === "enrolled" ? "已报名" : "未报名"}
                                  {count > 0 && <span className="ml-1 text-xs opacity-70">({count})</span>}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* 发送方式 */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">发送方式</label>
                <div className="flex gap-2">
                  {(["now", "later"] as const).map((t) => (
                    <button key={t} onClick={() => setForm((p) => ({ ...p, scheduleType: t }))} className={`flex-1 py-2.5 rounded-xl text-sm border-2 cursor-pointer transition-all whitespace-nowrap ${form.scheduleType === t ? "border-red-400 bg-red-50 text-red-600 font-medium" : "border-gray-100 text-gray-600 hover:border-gray-200"}`}>
                      <i className={`${t === "now" ? "ri-send-plane-line" : "ri-time-line"} mr-1.5`}></i>
                      {t === "now" ? "立即发送" : "定时发送"}
                    </button>
                  ))}
                </div>
                {form.scheduleType === "later" && (
                  <input type="datetime-local" value={form.scheduledAt} onChange={(e) => setForm((p) => ({ ...p, scheduledAt: e.target.value }))} className="w-full mt-2 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400" />
                )}
              </div>
            </div>

            <div className="flex justify-between items-center px-6 pb-6 pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-400">
                将发送给{" "}
                <span className="font-semibold text-gray-600">
                  {audience === null || audienceLoading ? "…" : getTargetCountForForm()}
                </span> 名会员
                {form.targetMode === "directed" && <span className="ml-1 text-teal-600 font-medium">（精准定向）</span>}
              </p>
              <div className="flex gap-2">
                <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap">取消</button>
                <button
                  onClick={() => void handleSend()}
                  disabled={
                    !form.title ||
                    !form.content ||
                    sending ||
                    (form.targetMode === "directed" && form.directedSubMode === "activity" && !form.directedActivityId) ||
                    ((form.targetMode === "group" ||
                      (form.targetMode === "directed" && form.directedSubMode === "level")) &&
                      (audience === null || audienceLoading))
                  }
                  className={`px-5 py-2 text-sm text-white rounded-xl cursor-pointer whitespace-nowrap transition-all ${sentSuccess ? "bg-green-500" : sending ? "bg-red-400 cursor-wait" : "bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"}`}
                >
                  {sentSuccess ? "✓ 已发送" : sending ? "发送中..." : form.scheduleType === "later" ? "确认定时" : "立即发送"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
